import { useState } from "react";
import { formatDateOnly } from "@/lib/date-only";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { apiGet } from "@/lib/api";
import { cn } from "@/lib/utils";
import {
  ListChecks,
  Plus,
  AlertCircle,
  Clock,
  CheckCircle,
  ArrowRight,
} from "lucide-react";

export const Route = createFileRoute("/_authenticated/corrective-actions")({
  component: CorrectiveActionsPage,
});

interface ActionSummary {
  id: string;
  title: string;
  description: string | null;
  priority: string;
  status: string;
  dueDate: string | null;
  sourceType: string | null;
  assignedToUserId: string | null;
  assignedToName: string | null;
  createdAt: string;
}

const STATUS_FILTERS = [
  { value: "all", label: "All" },
  { value: "open", label: "Open" },
  { value: "in_progress", label: "In Progress" },
  { value: "overdue", label: "Overdue" },
  { value: "closed", label: "Closed" },
] as const;

const PRIORITY_COLORS: Record<string, string> = {
  low: "bg-blue-100 text-blue-800",
  medium: "bg-yellow-100 text-yellow-800",
  high: "bg-orange-100 text-orange-800",
  critical: "bg-red-100 text-red-800",
};

const STATUS_ICONS: Record<string, typeof AlertCircle> = {
  open: AlertCircle,
  in_progress: Clock,
  overdue: AlertCircle,
  closed: CheckCircle,
};

function CorrectiveActionsPage() {
  const [statusFilter, setStatusFilter] = useState("all");

  const { data, isLoading } = useQuery({
    queryKey: ["corrective-actions", statusFilter],
    queryFn: () =>
      apiGet<{ data: ActionSummary[]; total: number }>(
        `/api/corrective-actions?status=${statusFilter}&limit=50`
      ),
  });

  const actions = data?.data || [];

  return (
    <div className="space-y-4 max-w-lg mx-auto">
      <div className="flex items-center justify-between pt-2">
        <h1 className="text-2xl font-bold text-text-primary">
          Corrective Actions
        </h1>
        <Link to="/corrective-actions/$actionId" params={{ actionId: "new" }}>
          <Button size="sm" className="bg-paris-red hover:bg-paris-red-dark gap-1">
            <Plus className="h-4 w-4" />
            New
          </Button>
        </Link>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-hide">
        {STATUS_FILTERS.map((filter) => (
          <button
            key={filter.value}
            type="button"
            onClick={() => setStatusFilter(filter.value)}
            className={cn(
              "inline-flex items-center whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors shrink-0",
              statusFilter === filter.value
                ? "bg-paris-red text-white"
                : "bg-card text-text-secondary border border-border hover:bg-muted/50 hover:text-text-primary"
            )}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {isLoading && (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-20 w-full rounded-xl" />
          ))}
        </div>
      )}

      {!isLoading && actions.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <ListChecks className="h-12 w-12 text-muted-foreground/40 mb-3" />
            <p className="text-lg font-medium text-muted-foreground">
              No corrective actions
            </p>
            <p className="text-sm text-muted-foreground/70 mt-1">
              Actions will appear here when created from incidents, inspections,
              or manually.
            </p>
          </CardContent>
        </Card>
      )}

      {!isLoading && actions.length > 0 && (
        <div className="space-y-2">
          {actions.map((action) => {
            const StatusIcon = STATUS_ICONS[action.status] || AlertCircle;
            return (
              <Link
                key={action.id}
                to="/corrective-actions/$actionId"
                params={{ actionId: action.id }}
              >
                <Card className="hover:bg-muted/30 transition-colors cursor-pointer">
                  <CardContent className="py-3 px-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">
                          {action.title}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge
                            className={cn(
                              "text-xs px-1.5 py-0",
                              PRIORITY_COLORS[action.priority]
                            )}
                          >
                            {action.priority}
                          </Badge>
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <StatusIcon className="h-3 w-3" />
                            {action.status.replace("_", " ")}
                          </span>
                        </div>
                        {action.assignedToName && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Assigned to: {action.assignedToName}
                          </p>
                        )}
                        {action.dueDate && (
                          <p className="text-xs text-muted-foreground">
                            Due: {formatDateOnly(action.dueDate)}
                          </p>
                        )}
                      </div>
                      <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0 mt-1" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
